import { useState } from 'react'
import { AlertTriangle, CheckCircle2, User as UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMe, useUpdateMe, type Me } from '../api/auth'

function initialsFromName(name: string, email: string): string {
  const source = (name || email || '').trim()
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

export function ProfilePage() {
  const { t } = useTranslation('auth')
  const { data: me, isLoading } = useMe()

  if (isLoading || !me) {
    return (
      <div className="text-sm text-text-secondary">
        {t('common.loading')}
      </div>
    )
  }

  // Key on user id only. We deliberately do NOT include display_name in the
  // key: the inner form already syncs against `me.user.display_name` via
  // its dirty/savedAt computation, and remounting on every save would wipe
  // the success banner before the user can see it. The id-only key still
  // remounts cleanly when the signed-in user actually changes.
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <ProfileForm key={me.user.id} me={me} />
    </div>
  )
}

// Backend validates rep_phone_number against ^\+\d{10,15}$
// (src/util/phone.is_e164). Mirror it client-side so the field blocks
// obviously-bad input before the round-trip. Empty clears the value.
const REP_PHONE_RE = /^\+\d{10,15}$/

function ProfileForm({ me }: { me: Me }) {
  const { t } = useTranslation('auth')
  const updateMe = useUpdateMe()
  const [displayName, setDisplayName] = useState(me.user.display_name ?? '')
  const [repPhone, setRepPhone] = useState(me.user.rep_phone_number ?? '')
  // Saved default Co-pilot call method. Empty maps to "no preference" (null).
  const [transport, setTransport] = useState<'' | 'pstn' | 'webrtc'>(
    me.user.copilot_call_transport ?? '',
  )
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const trimmed = displayName.trim()
  const nameDirty = trimmed !== (me.user.display_name ?? '').trim()

  const repTrimmed = repPhone.trim()
  const repPhoneValid = repTrimmed === '' || REP_PHONE_RE.test(repTrimmed)
  const phoneDirty = repTrimmed !== (me.user.rep_phone_number ?? '').trim()

  const transportDirty = transport !== (me.user.copilot_call_transport ?? '')

  const dirty = nameDirty || phoneDirty || transportDirty
  const canSave =
    dirty &&
    trimmed.length > 0 &&
    repPhoneValid &&
    !updateMe.isPending

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSavedAt(null)
    // Send only the changed field(s); empty phone clears to null.
    const body: {
      display_name?: string
      rep_phone_number?: string | null
      copilot_call_transport?: 'pstn' | 'webrtc' | null
    } = {}
    if (nameDirty) body.display_name = trimmed
    if (phoneDirty) body.rep_phone_number = repTrimmed === '' ? null : repTrimmed
    if (transportDirty)
      body.copilot_call_transport = transport === '' ? null : transport
    try {
      await updateMe.mutateAsync(body)
      setSavedAt(Date.now())
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const avatarUrl = me.user.avatar_url
  const initials = initialsFromName(me.user.display_name, me.user.email)

  return (
    <div className="mx-auto max-w-2xl">
      <form
        onSubmit={onSubmit}
        className="space-y-6 rounded-xl border border-border bg-surface p-6 shadow-sm"
      >
        <div className="flex items-center gap-4 border-b border-border pb-5">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-surface-elevated text-primary">
            <UserIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold">
              {t('profile.title')}
            </h1>
            <p className="text-sm text-text-secondary">
              {t('profile.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-16 w-16 rounded-full border border-border object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-border bg-surface-elevated text-lg font-semibold text-text-secondary">
              {initials}
            </div>
          )}
          <p className="text-xs text-text-secondary">{t('profile.avatarHint')}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t('profile.emailLabel')}
            </label>
            <input
              type="email"
              value={me.user.email}
              readOnly
              className="mt-1 w-full cursor-not-allowed rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-secondary"
            />
          </div>
          <div>
            <label
              htmlFor="display-name"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('profile.displayNameLabel')}
            </label>
            <input
              id="display-name"
              type="text"
              maxLength={100}
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value)
                setSavedAt(null)
              }}
              placeholder={t('profile.displayNamePlaceholder')}
              required
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="rep-phone"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('profile.repPhoneLabel')}
            </label>
            <input
              id="rep-phone"
              type="tel"
              inputMode="tel"
              value={repPhone}
              onChange={(e) => {
                setRepPhone(e.target.value)
                setSavedAt(null)
              }}
              placeholder="+13055551234"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-text-secondary">
              {t('profile.repPhoneHint')}
            </p>
            {repTrimmed !== '' && !repPhoneValid && (
              <p className="mt-1 text-xs text-error">
                {t('profile.repPhoneInvalid')}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="copilot-transport"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('profile.callMethodLabel')}
            </label>
            <select
              id="copilot-transport"
              value={transport}
              onChange={(e) => {
                setTransport(e.target.value as '' | 'pstn' | 'webrtc')
                setSavedAt(null)
              }}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="pstn">{t('profile.callMethodPhone')}</option>
              <option value="webrtc">{t('profile.callMethodBrowser')}</option>
            </select>
            <p className="mt-1 text-xs text-text-secondary">
              {t('profile.callMethodHint')}
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-error/50 bg-error/10 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-error" />
            <div className="text-sm text-text-secondary">{error}</div>
          </div>
        )}

        {savedAt && !dirty && (
          <div className="flex items-start gap-3 rounded-lg border border-success/50 bg-success/10 p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
            <div className="text-sm text-text-secondary">{t('profile.saved')}</div>
          </div>
        )}

        <div className="flex justify-end border-t border-border pt-5">
          <button
            type="submit"
            disabled={!canSave}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-background hover:bg-primary-hover disabled:opacity-50"
          >
            {updateMe.isPending ? t('profile.saving') : t('profile.save')}
          </button>
        </div>
      </form>
    </div>
  )
}
